import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ActivityIcon, ArrowRightIcon, LockIcon } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { TextInput } from '../../components/ui/TextInput';
import { EmailPreview } from '../../components/EmailPreview';
import { ExpiryRunway } from '../../components/ExpiryRunway';
import { Sparkline } from '../../components/Sparkline';
const uptimeBoard = [{
  domain: 'acme.com',
  availability: '100.00%',
  up: true,
  values: [212, 196, 231, 188, 204, 176, 219, 198, 241, 187, 208, 223, 195, 179, 214, 201]
}, {
  domain: 'shop.acme.com',
  availability: '99.97%',
  up: true,
  values: [265, 248, 301, 272, 259, 288, 244, 312, 267, 251, 279, 294, 261, 273, 256, 268]
}, {
  domain: 'api.acme.com',
  availability: '96.40%',
  up: false,
  values: [128, 141, 119, 134, 126, 147, 131, 122, 0, 0, 0, 0, 0, 0, 0, 0]
}];
const dayStages = [{
  step: 'Step 1',
  title: 'Add your domains',
  body: 'Type a domain and choose SSL, uptime, or both. No DNS changes, no code on your site, no agent to install.'
}, {
  step: 'Step 2',
  title: 'We watch them',
  body: 'Certificates are checked every morning at 08:00. Uptime is checked as often as every minute, from three regions.'
}, {
  step: 'Step 3',
  title: 'You get an email',
  body: 'Only when something needs you: an expiry date approaching, a site down, or a site back up. Otherwise, silence.'
}];
export function Landing() {
  const [domain, setDomain] = useState('');
  const [footerDomain, setFooterDomain] = useState('');
  const navigate = useNavigate();
  return <>
      <section className="bg-shell" data-id="element-1519" data-mp="17n8mb19x45hw">
        <div className="mx-auto grid w-full max-w-page items-center gap-14 px-5 pb-16 pt-16 sm:px-8 lg:grid-cols-[0.94fr_1.06fr] lg:pb-24 lg:pt-24" data-id="element-1520" data-mp="17o9vf07dihwz">
          <div data-id="element-1521" data-mp="f661ls3x33ml">
            <h1 className="text-[2.9rem] font-semibold leading-[1.05] tracking-[-0.02em] text-white sm:text-[3.5rem]" data-id="element-1522" data-mp="f5mhq5upi55x">
              SSL expiry and uptime monitoring,
              <span className="block text-white/45" data-id="element-1523" data-mp="26n2rmskseoy">by email.</span>
            </h1>
            <p className="mt-6 max-w-lg text-lg leading-relaxed text-white/65" data-id="element-1524" data-mp="24kdx635srlt">
              We check your SSL certificates every day and ping your sites every
              minute. When a certificate is about to expire or a site goes down,
              you get an email. That is the whole product.
            </p>

            <form className="mt-8 flex max-w-md flex-col gap-2.5 sm:flex-row" onSubmit={event => {
            event.preventDefault();
            navigate('/signup', {
              state: {
                domain
              }
            });
          }} data-id="element-1525" data-mp="3fd6pti72jz2a">
              <label htmlFor="hero-domain" className="sr-only" data-id="element-1526" data-mp="3p9rvm5qwxe76">
                Your domain
              </label>
              <TextInput id="hero-domain" placeholder="yourdomain.com" value={domain} onChange={event => setDomain(event.target.value)} className="h-11 border-transparent font-mono sm:flex-1" data-id="element-1527" data-mp="19mbboumn74j8" />
              <Button type="submit" variant="bright" size="lg" className="group" data-id="element-1528" data-mp="1h511z3tri8so">
                Check my certificate
                <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 ease-snap group-hover:translate-x-0.5" aria-hidden="true" data-id="element-1529" data-mp="136b4hn31673s" />
              </Button>
            </form>

            <p className="mt-4 text-[13px] text-white/50" data-id="element-1530" data-mp="1zuh3nuh6o7ao">
              From $2 a month · set up in a minute · cancel any time
            </p>
          </div>

          <ExpiryRunway data-id="element-1531" data-mp="1m0zw781rul10" />
        </div>
      </section>

      <section className="border-b border-line bg-surface" data-id="element-1532" data-mp="1q3csl5ump6l4">
        <div className="mx-auto w-full max-w-page px-5 py-16 sm:px-8 lg:py-20" data-id="element-1533" data-mp="2du8pghdxyrz8">
          <div className="max-w-2xl" data-id="element-1534" data-mp="2ds5q36qwfmhu">
            <h2 className="text-[1.9rem] font-semibold leading-tight tracking-tight text-ink sm:text-[2.3rem]" data-id="element-1535" data-mp="2ikyu1sejmqsv">
              We run two checks on your sites
            </h2>
            <p className="mt-3 text-base leading-relaxed text-ink-muted" data-id="element-1536" data-mp="pxap0kzycn1d">
              Add a domain, switch on the checks you want. You can use both on
              one site, or just one on many sites.
            </p>
          </div>

          <div className="mt-10 overflow-hidden rounded-card border border-line" data-id="element-1537" data-mp="gxnu8i1qafha">
            <div className="grid divide-y divide-line lg:grid-cols-2 lg:divide-x lg:divide-y-0" data-id="element-1538" data-mp="1dn4x6p9btnfa">
              <article className="p-6 sm:p-8" data-id="element-1539" data-mp="18na9svorffyz">
                <h3 className="flex items-center gap-2.5 text-base font-semibold text-ink" data-id="element-1540" data-mp="18ok55d48aa1x">
                  <LockIcon className="h-4 w-4 text-brand" aria-hidden="true" data-id="element-1541" data-mp="13v8z3e78pxb2" />
                  SSL certificate expiry
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-ink-muted" data-id="element-1542" data-mp="1wd4jj4ds8of0">
                  We read your certificate once a day and count down the days
                  left. You get an email at 30, 14, 7 and 1 day before it
                  expires — and again if the certificate breaks.
                </p>

                <div className="mt-6 flex items-end justify-between gap-4 rounded-card border border-warn-border bg-warn-soft p-5" data-id="element-1543" data-mp="3f7zbnbreuh1q">
                  <div data-id="element-1544" data-mp="3j8j6g8oih8kp">
                    <p className="font-mono text-[2.6rem] font-medium leading-none text-warn-ink" data-id="element-1545" data-mp="3jc6xn16hhiw9">
                      9
                    </p>
                    <p className="mt-2 font-mono text-xs text-warn-ink/80" data-id="element-1546" data-mp="3t91wjl6o5lb2">
                      days · shop.acme.com
                    </p>
                  </div>
                  <div className="flex gap-1.5" data-id="element-1547" data-mp="6snvo5qry5ye">
                    {['30d', '14d', '7d', '1d'].map((threshold, index) => <span key={threshold} className={`rounded border px-1.5 py-1 font-mono text-[11px] font-medium ${index > 1 ? 'border-warn-ink bg-warn-ink text-warn-soft' : 'border-warn-border text-warn-ink/70'}`} data-id="element-1548" data-mp="auiwl94smhbn" data-mpi={index}>
                        {threshold}
                      </span>)}
                  </div>
                </div>
              </article>

              <article className="p-6 sm:p-8" data-id="element-1549" data-mp="2qnhijam05qtl">
                <h3 className="flex items-center gap-2.5 text-base font-semibold text-ink" data-id="element-1550" data-mp="2luemmvtr65a8">
                  <ActivityIcon className="h-4 w-4 text-brand" aria-hidden="true" data-id="element-1551" data-mp="2zlbgepzf0siv" />
                  Uptime
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-ink-muted" data-id="element-1552" data-mp="39k0qvxozfap3">
                  We request your site as often as every minute and record the
                  response time. If it fails a few times in a row, you get an
                  email — and another one when it recovers.
                </p>

                <ul className="mt-6 divide-y divide-line overflow-hidden rounded-card border border-line" data-id="element-1553" data-mp="1a7y8yrw0aodj">
                  {uptimeBoard.map(row => <li key={row.domain} className="flex items-center gap-3 px-4 py-3.5" data-id="element-1554" data-mp="3lwulsjhwaawg">
                      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${row.up ? 'bg-healthy' : 'bg-critical'}`} aria-hidden="true" data-id="element-1555" data-mp="3c3ngszr2gwww" />
                      <span className="min-w-0 flex-1 truncate font-mono text-[13px] text-ink" data-id="element-1556" data-mp="3833k1xaenvba">
                        {row.domain}
                      </span>
                      <Sparkline values={row.values} count={16} label={`Response times for ${row.domain}`} data-id="element-1557" data-mp="2t7nplr5wxxed" />
                      <span className={`w-[66px] shrink-0 text-right font-mono text-[13px] ${row.up ? 'text-ink-muted' : 'text-critical-ink'}`} data-id="element-1558" data-mp="rf97aqzgiqxf">
                        {row.availability}
                      </span>
                    </li>)}
                </ul>
              </article>
            </div>

            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 bg-shell px-6 py-4 sm:px-8" data-id="element-1559" data-mp="36hfmps91hyhq">
              <p className="font-mono text-[13px] font-medium text-brand-bright" data-id="element-1560" data-mp="2wlma68vbm4zy">
                1 check on 1 domain = 1 service
              </p>
              <p className="text-[13px] text-white/60" data-id="element-1561" data-mp="2sj7rppvkd797">
                Plans are sold in services. 4 services = 2 sites with both
                checks, or 4 sites with one.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-line bg-canvas" data-id="element-1562" data-mp="ltjc6nvh8ktv">
        <div className="mx-auto w-full max-w-page px-5 py-16 sm:px-8 lg:py-20" data-id="element-1563" data-mp="lt1b9dxvdifm">
          <div className="max-w-2xl" data-id="element-1564" data-mp="gvhti41yqnix">
            <h2 className="text-[1.9rem] font-semibold leading-tight tracking-tight text-ink sm:text-[2.3rem]" data-id="element-1565" data-mp="gsdnr28unjq5">
              How it works
            </h2>
            <p className="mt-3 text-base leading-relaxed text-ink-muted" data-id="element-1566" data-mp="tc0tvhdib5xx">
              You add your domains once. After that there is nothing to open and
              nothing to remember.
            </p>
          </div>

          <ol className="mt-10 grid gap-8 md:grid-cols-3" data-id="element-1567" data-mp="132c4fmrp4dds">
            {dayStages.map(stage => <li key={stage.step} className="border-t border-line-strong pt-5" data-id="element-1568" data-mp="5gy333v56set">
                <p className="font-mono text-[11px] uppercase tracking-[0.12em] text-brand" data-id="element-1569" data-mp="aezmjpwhctqt">
                  {stage.step}
                </p>
                <h3 className="mt-2.5 text-lg font-semibold tracking-tight text-ink" data-id="element-1570" data-mp="kce11jyuf8gh">
                  {stage.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-ink-muted" data-id="element-1571" data-mp="1qremuwu5ma9c">
                  {stage.body}
                </p>
              </li>)}
          </ol>
        </div>
      </section>

      <section className="border-b border-line bg-surface" data-id="element-1572" data-mp="2tt5y9pydbtr5">
        <div className="mx-auto grid w-full max-w-page items-center gap-12 px-5 py-16 sm:px-8 lg:grid-cols-[0.85fr_1.15fr] lg:py-20" data-id="element-1573" data-mp="2xvsrnvaotmqg">
          <div data-id="element-1574" data-mp="32nkafjnlqo61">
            <h2 className="text-[1.9rem] font-semibold leading-tight tracking-tight text-ink sm:text-[2.3rem]" data-id="element-1575" data-mp="32nu4bibrpfh0">
              This is the email you get
            </h2>
            <p className="mt-4 text-base leading-relaxed text-ink-muted" data-id="element-1576" data-mp="2avv24e819gfx">
              It tells you the domain, who issued the certificate, exactly how
              many days are left, and when we will email you again. No login
              needed to understand it.
            </p>
            <p className="mt-4 text-sm leading-relaxed text-ink-muted" data-id="element-1577" data-mp="2ouup7hfwdn9z">
              You can send alerts to as many people as you like — your
              developer, your agency, whoever renews the certificate.
            </p>
          </div>

          <EmailPreview kind="expiry" domain="shop.acme.com" days={9} recipients={['priya@acme.com', 'ops@acme.com']} reminderCadence={[7, 3, 1]} data-id="element-1578" data-mp="43c0lg9ab152" />
        </div>
      </section>

      <section className="bg-shell" data-id="element-1579" data-mp="1ex4kiw25es58">
        <div className="mx-auto grid w-full max-w-page items-center gap-10 px-5 py-16 sm:px-8 lg:grid-cols-[1fr_auto]" data-id="element-1580" data-mp="1ew32u8qmn7ej">
          <div data-id="element-1581" data-mp="19xrpvp51cgs3">
            <h2 className="text-[1.9rem] font-semibold leading-tight tracking-tight text-white sm:text-[2.2rem]" data-id="element-1582" data-mp="19vyqmuwr4yu4">
              Start monitoring your first site
            </h2>
            <p className="mt-3 max-w-xl text-base leading-relaxed text-white/60" data-id="element-1583" data-mp="10vsaf19o5kr9">
              <span className="font-mono text-white" data-id="element-1584" data-mp="vz1t6am4oj49">$2</span> a month for two
              services. No contract, cancel any time.
            </p>
          </div>

          <form className="flex w-full flex-col gap-2.5 sm:flex-row lg:w-auto" onSubmit={event => {
          event.preventDefault();
          navigate('/signup', {
            state: {
              domain: footerDomain
            }
          });
        }} data-id="element-1585" data-mp="2qc1dgh1ge1hv">
            <label htmlFor="cta-domain" className="sr-only" data-id="element-1586" data-mp="jmpw2pvj056x">
              Your domain
            </label>
            <TextInput id="cta-domain" placeholder="yourdomain.com" value={footerDomain} onChange={event => setFooterDomain(event.target.value)} className="h-11 border-transparent font-mono sm:w-[220px]" data-id="element-1587" data-mp="ogu83njnf3ev" />
            <Button type="submit" variant="bright" size="lg" data-id="element-1588" data-mp="12fbo0z3bbbm8">
              Start monitoring
            </Button>
          </form>
        </div>
      </section>
    </>;
}